import { useTranslation } from 'react-i18next';
import InputLabel from 'components/others/InputLabel';
import { useSettings } from 'context/SettingsContext';
import vconst from 'utils/helpers/structs/vconst';
import { tDirName } from 'utils/helpers/i18n/tDirName';
import type { TelegramObjectEventType, TelegramTaskRoleType } from '@econtractor/types';
import type { UserTabProps } from '../../types';

type ListOption = { value: string | number; label: string };

const UserTabTelegram = ({ user, dispatchUser }: UserTabProps) => {
    const { t } = useTranslation(['common', 'Leads']);
    const { t: tWh } = useTranslation(['Warehouses']);
    const { localSettings } = useSettings();
    const options =
        localSettings?.DIRS?.LeadStatus?.map((lStat) => ({
            value: lStat.ID,
            label: tDirName(t, lStat.i18nKey, lStat.statusName) || lStat.statusName,
        })) || [];

    const telegramEvents = user?.telegramEvents || {};
    const selectedStatuses = telegramEvents.leadStatuses || [];
    const selectedTaskRoles = telegramEvents.taskRoles || [];
    const selectedObjectEvents = telegramEvents.objectEvents || [];
    const selectedWarehouses = telegramEvents.warehouses || [];

    const baseWarehouses = (localSettings?.WAREHOUSES || []).filter(
        (wh) =>
            wh.objectId === 0 &&
            !(Number(wh.customId) > 0 && Number(wh.customDeleted)),
    );
    const orderedWarehouses = [
        baseWarehouses.find((wh) => wh.ID === vconst.waherouseMainId),
        baseWarehouses.find((wh) => wh.ID === vconst.waherouseWriteOffId),
        ...baseWarehouses.filter((wh) => ![vconst.waherouseMainId, vconst.waherouseWriteOffId].includes(wh.ID)),
    ].filter(Boolean);

    const warehouseOptions = orderedWarehouses.map((wh) => {
        let label = '';
        if (Number(wh!.customId) > 0) {
            label = wh!.customName || `#${wh!.customId}`;
        } else if (!wh!.teamId && !wh!.objectId) {
            label = wh!.ID === vconst.waherouseMainId ? tWh('mainWarehouse') : tWh('writeOffWarehouse');
        } else {
            label = wh!.teamId ? wh!.teamName : wh!.objectName || wh!.clientName || '';
        }
        return { value: wh!.ID, label };
    });

    const taskRoleOptions = [
        { value: 'executor', labelKey: 'Users.taskRoles.executor' },
        { value: 'controller', labelKey: 'Users.taskRoles.controller' },
    ].map((o) => ({ value: o.value, label: t(o.labelKey) }));

    const objectEventOptions = [
        { value: 'create', labelKey: 'Users.objectEvents.create' },
        { value: 'taken_into_work', labelKey: 'Users.objectEvents.taken_into_work' },
        { value: 'completed_by_brigade', labelKey: 'Users.objectEvents.completed_by_brigade' },
        { value: 'cancelled', labelKey: 'Users.objectEvents.cancelled' },
        { value: 'finished', labelKey: 'Users.objectEvents.finished' },
        { value: 'vat', labelKey: 'Users.objectEvents.vat' },
        { value: 'team', labelKey: 'Users.objectEvents.team' },
        { value: 'name', labelKey: 'Users.objectEvents.name' },
        { value: 'deadline', labelKey: 'Users.objectEvents.deadline' },
        { value: 'created_date', labelKey: 'Users.objectEvents.created_date' },
        { value: 'finished_date', labelKey: 'Users.objectEvents.finished_date' },
        { value: 'info', labelKey: 'Users.objectEvents.info' },
        { value: 'warehouses', labelKey: 'Users.objectEvents.warehouses' },
        { value: 'services', labelKey: 'Users.objectEvents.services' },
        { value: 'other', labelKey: 'Users.objectEvents.other' },
        { value: 'description', labelKey: 'Users.objectEvents.description' },
    ].map((o) => ({ value: o.value, label: t(o.labelKey) }));

    const renderMultiCheckboxList = (
        field: 'leadStatuses' | 'taskRoles' | 'objectEvents' | 'warehouses',
        listOptions: ListOption[],
        selectedValues: Array<string | number>,
    ) => (
        <div className="border rounded p-2" style={{ overflowY: 'auto' }}>
            {listOptions.length ? (
                listOptions.map((opt) => (
                    <label key={String(opt.value)} className="form-check d-flex align-items-center mb-2">
                        <input
                            className="form-check-input me-2"
                            type="checkbox"
                            checked={selectedValues.includes(opt.value)}
                            onChange={(e) => {
                                const nextValues = e.currentTarget.checked
                                    ? [...selectedValues, opt.value]
                                    : selectedValues.filter((v) => v !== opt.value);

                                dispatchUser({
                                    type: 'SET_TELEGRAM_EVENTS_FIELD',
                                    field,
                                    value: nextValues,
                                });
                            }}
                        />
                        <span className="form-check-label mb-0">{opt.label}</span>
                    </label>
                ))
            ) : (
                <div className="text-muted small">{t('General.noData')}</div>
            )}
        </div>
    );

    return (
        <>
            <InputLabel title={t('Users.fieldUserTelegram')} rightElement={null}>
                <input
                    className="form-control"
                    type="text"
                    maxLength={101}
                    placeholder="@username"
                    value={user?.telegram ? `@${user.telegram}` : ''}
                    onChange={(e) => {
                        const val = e.currentTarget.value.replace(/^@/, '').trim();
                        dispatchUser({
                            type: 'SET_GENERAL_FIELD',
                            field: 'telegram',
                            value: val,
                        });
                    }}
                />
            </InputLabel>

            <InputLabel title={t('Users.fieldTelegramLeadEvents')} rightElement={null}>
                {renderMultiCheckboxList('leadStatuses', options, selectedStatuses)}
            </InputLabel>

            <InputLabel title={t('Users.fieldTelegramTaskEvents')} rightElement={null}>
                {renderMultiCheckboxList(
                    'taskRoles',
                    taskRoleOptions,
                    selectedTaskRoles as TelegramTaskRoleType[],
                )}
            </InputLabel>

            <InputLabel title={t('Users.fieldTelegramObjectEvents')} rightElement={null}>
                {renderMultiCheckboxList(
                    'objectEvents',
                    objectEventOptions,
                    selectedObjectEvents as TelegramObjectEventType[],
                )}
            </InputLabel>

            <InputLabel title={t('Users.fieldTelegramWarehouseEvents')} rightElement={null}>
                {renderMultiCheckboxList('warehouses', warehouseOptions, selectedWarehouses)}
            </InputLabel>
        </>
    );
};

export default UserTabTelegram;
